import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { inlineHeadingHtml } from '../../sanitize/inlineHeadingHtml.js';
import { escapeHtml, num, resolveColor, str } from './settingValue.js';
/**
 * The inspector fields for one heading-highlight engine (`headingHighlight1`/`2`): a style select (the 8
 * styles + none) plus the colour + gradient the common styles need (the bg/underline/line styles render
 * with the engine's sensible defaults). Spread into a schema's `fields[]`. Engine 1 defaults to a solid
 * colour and targets **bold** words; engine 2 defaults to none and targets *italic* words.
 */
export function headingHighlightFields(n, group = 'Heading highlight') {
    const p = `headingHighlight${n}`;
    return [
        {
            id: p,
            type: 'select',
            label: `Heading words highlight ${n}`,
            group,
            default: n === 1 ? 'solid-color' : 'none',
            info: n === 1
                ? 'Wrap words in ** ** in the heading to style them with this.'
                : 'Wrap words in * * in the heading to style them with this.',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid-color', label: 'Color' },
                { value: 'gradient-color', label: 'Gradient' },
                { value: 'bg-color', label: 'Background' },
                { value: 'underline-solid', label: 'Underline' },
                { value: 'underline-gradient', label: 'Gradient underline' },
                { value: 'line-1', label: 'Line 1' },
                { value: 'line-2', label: 'Line 2' },
                { value: 'line-3', label: 'Line 3' },
            ],
        },
        {
            id: `${p}Color`,
            type: 'color',
            label: 'Highlight text color',
            group,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: p, equals: ['solid-color', 'bg-color'] },
        },
        {
            id: `${p}Gradient`,
            type: 'gradient',
            label: 'Highlight gradient',
            group,
            default: DEFAULT_HL_GRADIENT,
            visibleWhen: { field: p, equals: 'gradient-color' },
        },
        // ── Background-pill sub-knobs (Shrine title_highlight_N_bg-color + highlighted-N_*) ───────────
        {
            id: `${p}BgColor`,
            type: 'color',
            label: 'Highlight background',
            group,
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}Radius`,
            type: 'range',
            label: 'Background radius',
            group,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 8,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}BorderWidth`,
            type: 'range',
            label: 'Background border width',
            group,
            min: 0,
            max: 6,
            step: 1,
            unit: 'px',
            default: 0,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}BorderStyle`,
            type: 'select',
            label: 'Background border style',
            group,
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            // Shrine gates on (bg-color AND border_width>0); VisibleWhen is single-field, so we key the
            // dominant condition (a non-zero border width) — same pattern as the success/error border-color.
            visibleWhen: { field: `${p}BorderWidth`, notEquals: 0 },
        },
        {
            id: `${p}BorderColor`,
            type: 'color',
            label: 'Background border color',
            group,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: `${p}BorderWidth`, notEquals: 0 },
        },
        {
            id: `${p}PaddingBlock`,
            type: 'range',
            label: 'Background padding (block)',
            group,
            min: 0,
            max: 1,
            step: 0.1,
            unit: 'em',
            default: 0.3,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}PaddingInline`,
            type: 'range',
            label: 'Background padding (inline)',
            group,
            min: 0,
            max: 1,
            step: 0.1,
            unit: 'em',
            default: 0.3,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        // ── Underline sub-knobs (Shrine title_highlight_N_underline-*) ───────────────────────────────
        {
            id: `${p}UnderlineThickness`,
            type: 'range',
            label: 'Underline thickness',
            group,
            min: 0,
            max: 6,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: { field: p, equals: ['underline-solid', 'underline-gradient'] },
        },
        {
            id: `${p}UnderlineColor`,
            type: 'color',
            label: 'Underline color',
            group,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: p, equals: 'underline-solid' },
        },
        {
            // Shrine title_highlight_N_underline-color--gradient — the gradient used when the underline
            // style is the gradient variant. Separate knob from the solid UnderlineColor above.
            id: `${p}UnderlineGradient`,
            type: 'gradient',
            label: 'Underline gradient',
            group,
            default: DEFAULT_HL_GRADIENT,
            visibleWhen: { field: p, equals: 'underline-gradient' },
        },
        {
            id: `${p}UnderlinePosition`,
            type: 'range',
            label: 'Underline position',
            group,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 25,
            visibleWhen: { field: p, equals: ['underline-solid', 'underline-gradient'] },
        },
        // ── "Line" / handwritten sub-knobs (Shrine title_highlight_N_handwritten-*) ──────────────────
        {
            id: `${p}LineColor`,
            type: 'color',
            label: 'Line color',
            group,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: p, equals: ['line-1', 'line-2', 'line-3'] },
        },
        {
            id: `${p}LineWidth`,
            type: 'range',
            label: 'Line stroke width',
            group,
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 3,
            visibleWhen: { field: p, equals: ['line-1', 'line-2', 'line-3'] },
        },
        {
            id: `${p}LinePosition`,
            type: 'range',
            label: 'Line position',
            group,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 60,
            visibleWhen: { field: p, equals: ['line-1', 'line-2', 'line-3'] },
        },
    ];
}
export const DEFAULT_HL_GRADIENT = 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)';
/**
 * Read one highlight engine's settings (prefix `headingHighlight1`/`2`) into a resolved config.
 * Colour fallbacks = the schema defaults (theme accent / accent-2), never Shrine's demo blue — an untouched
 * field must render what the inspector shows (founder, 2026-09-11).
 */
export function readHighlight(s, p) {
    return {
        type: str(s[p], 'none'),
        gradient: str(s[`${p}Gradient`], DEFAULT_HL_GRADIENT),
        bgColor: resolveColor(s[`${p}BgColor`], 'var(--aqx-theme-accent-2)'),
        color: resolveColor(s[`${p}Color`], 'var(--aqx-theme-accent)'),
        radius: num(s[`${p}Radius`], 8),
        borderWidth: num(s[`${p}BorderWidth`], 0),
        borderStyle: str(s[`${p}BorderStyle`], 'solid'),
        borderColor: resolveColor(s[`${p}BorderColor`], 'var(--aqx-theme-accent)'),
        paddingBlock: num(s[`${p}PaddingBlock`], 0.3),
        paddingInline: num(s[`${p}PaddingInline`], 0.3),
        underlineThickness: num(s[`${p}UnderlineThickness`], 2),
        underlineColor: resolveColor(s[`${p}UnderlineColor`], 'var(--aqx-theme-accent)'),
        underlineGradient: str(s[`${p}UnderlineGradient`], DEFAULT_HL_GRADIENT),
        underlinePosition: num(s[`${p}UnderlinePosition`], 25),
        lineColor: resolveColor(s[`${p}LineColor`], 'var(--aqx-theme-accent)'),
        lineWidth: num(s[`${p}LineWidth`], 3),
        linePosition: num(s[`${p}LinePosition`], 60),
    };
}
/** CSS rules applying one highlight engine to a selector (the heading's `strong`/`em` words). */
export function highlightCss(selector, c) {
    switch (c.type) {
        case 'solid-color':
            return `${selector}{color:${c.color}}`;
        case 'gradient-color':
            return `${selector}{background-image:${c.gradient};-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent}`;
        case 'bg-color': {
            const border = c.borderWidth
                ? `border:${c.borderWidth}px ${c.borderStyle} ${c.borderColor};`
                : '';
            return `${selector}{background-color:${c.bgColor};color:${c.color};border-radius:${c.radius}px;padding:${c.paddingBlock}em ${c.paddingInline}em;${border}box-decoration-break:clone;-webkit-box-decoration-break:clone}`;
        }
        case 'underline-solid': {
            // Position sits the line in the lower band of the text (0 → just below centre, 100 → at the very
            // bottom) so it reads as an underline, not an overline.
            const y = Math.round(60 + c.underlinePosition * 0.4);
            return `${selector}{padding-bottom:0.12em;background-image:linear-gradient(${c.underlineColor},${c.underlineColor});background-repeat:no-repeat;background-size:100% ${c.underlineThickness}px;background-position:0 ${y}%}`;
        }
        case 'underline-gradient': {
            const y = Math.round(60 + c.underlinePosition * 0.4);
            return `${selector}{padding-bottom:0.12em;background-image:${c.underlineGradient};background-repeat:no-repeat;background-size:100% ${c.underlineThickness}px;background-position:0 ${y}%}`;
        }
        case 'line-1':
        case 'line-2':
        case 'line-3': {
            // The 3 "Line" styles are decorative underlines: straight, double and wavy (hand-drawn feel).
            const decoStyle = c.type === 'line-2' ? 'double' : c.type === 'line-3' ? 'wavy' : 'solid';
            const offset = (0.08 + (c.linePosition / 100) * 0.18).toFixed(3);
            return `${selector}{text-decoration-line:underline;text-decoration-style:${decoStyle};text-decoration-color:${c.lineColor};text-decoration-thickness:${c.lineWidth}px;text-underline-offset:${offset}em}`;
        }
        default:
            return '';
    }
}
/**
 * Heading inline markup: escape, then **bold**→<strong> (highlight 1) and *italic*→<em> (highlight 2).
 *
 * DEFENSIVE (incident-hardened): a heading field migrated text→richtext can arrive at runtime as a
 * RichtextValue OBJECT (or null) from an older saved document, even though the param is typed `string`.
 * Calling `.replace` on a non-string throws — and since SSR error boundaries do NOT catch during
 * renderToString, ONE such throw takes down the WHOLE page. So we never assume a string: non-strings are
 * delegated to {@link richHeading} (renders the object's html, or degrades to ''), so a mis-shaped heading
 * DEGRADES instead of crashing the page.
 */
export function parseHeadingMarkup(heading) {
    if (typeof heading !== 'string')
        return richHeading(heading);
    return escapeHtml(heading)
        .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+?)\*/g, '<em>$1</em>');
}
/**
 * A heading value → inline HTML for an `<h*>`. Headings are authored like the Text block (a richtext field):
 * Bold → `<strong>` = highlight 1, Italic → `<em>` = highlight 2 (highlightCss targets `.__heading strong` /
 * `em`). We sanitize, then FLATTEN the editor's block wrappers — `<p>`, and above all a nested `<h2>`/`<h3>`
 * from the toolbar's H2/H3 buttons, which the storefront's document parser ejects OUT of the heading (it
 * then loses the block's font-size and drops to 16px, while the builder's fragment parser kept it nested and
 * looked fine). See `inlineHeadingHtml`.
 *
 * BACKWARD-COMPAT: instances saved before a section moved heading text→richtext still carry a plain STRING
 * with `**`/`*` markup, so a string falls back to {@link parseHeadingMarkup} (same `<strong>`/`<em>` output).
 * New + old data both render; no migration needed.
 */
export function richHeading(v) {
    if (!v)
        return '';
    if (typeof v === 'string')
        return parseHeadingMarkup(v);
    // Defensive: only render a well-formed html richtext value. Anything else (missing/odd `html`, a different
    // format, or an unexpected non-object) degrades to '' rather than letting sanitizeRichtextHtml throw.
    if (typeof v !== 'object' || v.format !== 'html' || typeof v.html !== 'string')
        return '';
    return inlineHeadingHtml(sanitizeRichtextHtml(v.html));
}
//# sourceMappingURL=headingHighlight.js.map